import { fn } from '@ember/helper';
import {
  PrismAccordion,
  PrismBadge,
  PrismBanner,
  PrismButton,
  PrismComboSelectInput,
  PrismIcon,
  PrismIconBrand,
  PrismLoadingBar,
  PrismMessage,
  PrismNumberInput,
  PrismSelectInput,
  PrismToggle,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, eq, or } from 'ember-truth-helpers';
import GeneralButtonsButtonSelectable from 'lumen/components/general/buttons/button-selectable';
import GeneralDismissibleBanner from 'lumen/components/general/dismissible-banner';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import HotelPricingAssistantPmsSsConnectPmsButton from 'lumen/components/hotel/pricing-assistant/pms-ss-connect-pms-button';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import SettingsPmsIntegrationRoomtypeHierarchyTreeVisualisation from 'lumen/components/settings/pms-integration/roomtype-hierarchy-tree-visualisation';
import SettingsPmsIntegrationRoomtypeOffset from 'lumen/components/settings/pms-integration/roomtype-offset';
import SettingsPmsIntegrationSeasonalRoomHierarchySection from 'lumen/components/settings/pms-integration/seasonal-room-hierarchy-section';
import {
  NUMBER_INPUT_MAX_VALUE,
  PMS_SETTINGS_OFFSET_TYPES,
  RATE_TYPES,
} from 'lumen/utils/constants/pricing-assistant-constants';

<template>
  <SettingsPageTemplate
    @hasAccountSelector={{true}}
    @title={{t "pricing_assistant.pms_integration.settings.main_title"}}
  >
    <:headerAfter>
      {{! Show CC toggle in case PMS is integrated and user has edit rights }}
      {{#if
        (and
          @controller.pricingAssistantPmsSs.isPricingAssistantPMSIntegrated
          @controller.access.hasPricingAssistantSettingsEditRights
        )
      }}
        <div class="flex flex-row items-center gap-100">
          <PrismToggle
            @isChecked={{@controller.caxxxPmsSettings.customer_care_access_given}}
            @isDisabled={{@controller.isCustomerCareAccessToggleDisabled}}
            @label={{t "pricing_assistant.customer_care_access.label"}}
            @onChange={{@controller.toggleCustomerCareAccess}}
          />

          <GeneralIconWithTooltip
            @side="bottom"
            @size="small"
            @tooltipContent={{@controller.customerCareAccessToggleMessage}}
          />
        </div>
      {{/if}}
    </:headerAfter>
    <:default>
      <section class="card-tile card-tile--pms-integration">
        {{! The route only performs the rate preference task for an integrated PMS,
            so a running instance implies integrated. It gates the whole body because
            `PrismIconBrand` asserts on an undefined channel and `preferredChannelId`
            feeds three of them }}
        {{#if @controller.model.ratePreferenceTaskResult.isRunning}}
          <PrismLoadingBar />
        {{else if
          @controller.pricingAssistantPmsSs.isPricingAssistantPMSIntegrated
        }}
          {{#if
            (or
              @controller.arePmsPrefillOptionsLoading
              @controller.arePmsSettingsUpdating
            )
          }}
            <PrismLoadingBar />
          {{/if}}

          <div class="pms-settings flex flex-row">
            <div class="pms-settings__panel flex flex-col">
              {{! Extra banner that displays a generic warning message if the result of PMS Settings validation is negative }}
              {{! The simulation sees if the current settings are able to generate recommendations. If not, we assume that something is wrong }}
              {{#if
                @controller.showPmsSettingsValueInvalidAfterSimulationBanner
              }}
                <PrismBanner
                  @bannerMessage={{t
                    "pricing_assistant.settings.pms_integrations.simulate_settings_warning"
                  }}
                  @bannerStyle="warning"
                  @position="top"
                />
              {{/if}}
              <div class="pms-settings__panel__content">

                {{! Rates settings }}
                <div class="pms-panel-section">
                  <PrismAccordion
                    @initiallyOpened={{true}}
                    @title={{t
                      "pricing_assistant.settings.section.rates.header"
                    }}
                  >
                    <div class="pms-panel-section__content">
                      {{! Root rate }}
                      <div class="flex flex-col">
                        <div class="mb-200 flex flex-row items-center">
                          <p class="text-200--bold mr-100">{{t
                              "pricing_assistant.settings.panel.section.rates.base_rate"
                            }}</p>
                          <GeneralIconWithTooltip
                            @side="right"
                            @tooltipContent={{t
                              "pricing_assistant.settings.panel.section.rates.base_rate_explanation"
                            }}
                          />
                        </div>

                        <PrismComboSelectInput
                          @disabled={{or
                            @controller.arePmsPrefillOptionsLoading
                            @controller.access.isPricingAssistantPmsSettingsActionOrInputDisabled
                          }}
                          @isSearchable={{true}}
                          @onChange={{@controller.updateBaseRate}}
                          @options={{@controller.baseRateOptions}}
                          @value={{@controller.selectedBaseRate}}
                        />
                      </div>

                      {{! Meal type options }}
                      <div class="ml-800">
                        <p class="text-200--bold mb-200">{{t
                            "pricing_assistant.settings.panel.section.rates.meal_type"
                          }}</p>
                        <div class="flex flex-row items-center justify-start">
                          {{#each @controller.pmsMealtypes as |mealtype|}}
                            <GeneralButtonsButtonSelectable
                              @iconName={{mealtype.icon}}
                              @iconSize="medium"
                              @isDisabled={{@controller.access.isPricingAssistantPmsSettingsActionOrInputDisabled}}
                              @isSelected={{eq
                                @controller.selectedPmsMealtype
                                mealtype.id
                              }}
                              @onClick={{fn
                                @controller.updateSelectedPmsMealtype
                                mealtype.id
                              }}
                            >
                              <PrismTooltip
                                @side="bottom"
                                @text={{mealtype.title}}
                              />
                            </GeneralButtonsButtonSelectable>
                          {{/each}}
                        </div>
                      </div>

                      {{! Occupancy options }}
                      <div class="ml-800">
                        <p class="text-200--bold mb-200">{{t
                            "pricing_assistant.settings.panel.section.rates.occupancy"
                          }}</p>
                        <div class="flex flex-col gap-200">
                          <div class="pms-occupancy-row flex items-center">
                            <p
                              class="text-200--regular pms-occupancy-row__occupancy-label"
                            >
                              {{t
                                "pricing_assistant.settings.panel.section.rates.occupancy.base_occupancy"
                              }}
                            </p>

                            <div class="pms-occupancy-row__base-occupancy">
                              <PrismSelectInput
                                @disabled={{or
                                  @controller.arePmsPrefillOptionsLoading
                                  @controller.access.isPricingAssistantPmsSettingsActionOrInputDisabled
                                }}
                                @onChange={{@controller.updatePmsBaseOccupancy}}
                                @options={{@controller.pmsBaseOccupancyOptions}}
                                @value={{@controller.selectedPmsBaseOccupancy}}
                              />
                            </div>

                          </div>
                          {{#if @controller.pmsOccupancyGuestMapping}}
                            <div class="pms-occupancy-row flex items-center">
                              <div
                                class="pms-occupancy-row__occupancy-label flex items-center justify-start gap-400"
                              >
                                <PrismIcon
                                  @color="inherit"
                                  @name={{@controller.formattedPmsGuest.iconName}}
                                  @size="small"
                                />
                                <p
                                  class="text-200--regular"
                                >{{@controller.formattedPmsGuest.labelValue}}</p>
                              </div>

                              <div class="pms-panel-section__input">
                                <PrismNumberInput
                                  @disabled={{@controller.access.isPricingAssistantPmsSettingsActionOrInputDisabled}}
                                  @inlineLabel={{if
                                    (eq
                                      @controller.selectedPmsGuestOffsetType.id
                                      PMS_SETTINGS_OFFSET_TYPES.RELATIVE
                                    )
                                    "%"
                                    @controller.hotelAndMarketService.hotel.currencySymbol
                                  }}
                                  @isTouched={{true}}
                                  @max={{NUMBER_INPUT_MAX_VALUE}}
                                  @onValueChange={{@controller.updatePmsGuestOffsetValue}}
                                  @removePadding={{true}}
                                  @required={{true}}
                                  @value={{@controller.formattedPmsGuestOffsetValue}}
                                />
                              </div>

                              <div class="ml-200">
                                <PrismSelectInput
                                  @disabled={{@controller.access.isPricingAssistantPmsSettingsActionOrInputDisabled}}
                                  @onChange={{@controller.updatePmsGuestOffsetType}}
                                  @options={{@controller.offsetTypeOptions}}
                                  @value={{@controller.selectedPmsGuestOffsetType}}
                                />
                              </div>
                            </div>

                            <div class="pms-occupancy-row flex items-center">
                              <div
                                class="pms-occupancy-row__occupancy-label flex items-center justify-start gap-400"
                              >
                                <PrismIcon
                                  @color="inherit"
                                  @name="user-add"
                                  @size="small"
                                />
                                <p class="text-200--regular">{{t
                                    "rates.extra_person"
                                  }}</p>
                              </div>

                              <div class="pms-panel-section__input">
                                <PrismNumberInput
                                  @disabled={{@controller.access.isPricingAssistantPmsSettingsActionOrInputDisabled}}
                                  @inlineLabel={{if
                                    (eq
                                      @controller.selectedPmsExtraGuestOffsetType.id
                                      PMS_SETTINGS_OFFSET_TYPES.RELATIVE
                                    )
                                    "%"
                                    @controller.hotelAndMarketService.hotel.currencySymbol
                                  }}
                                  @isTouched={{true}}
                                  @max={{NUMBER_INPUT_MAX_VALUE}}
                                  @onValueChange={{@controller.updatePmsExtraGuestOffsetValue}}
                                  @removePadding={{true}}
                                  @required={{true}}
                                  @value={{@controller.formattedPmsExtraGuestOffsetValue}}
                                />
                              </div>

                              <div class="ml-200">
                                <PrismSelectInput
                                  @disabled={{@controller.access.isPricingAssistantPmsSettingsActionOrInputDisabled}}
                                  @onChange={{@controller.updatePmsExtraGuestOffsetType}}
                                  @options={{@controller.offsetTypeOptions}}
                                  @value={{@controller.selectedPmsExtraGuestOffsetType}}
                                />
                              </div>

                            </div>
                          {{/if}}
                        </div>
                      </div>

                      {{! Booking.com matching rate type }}
                      <div class="ml-800">
                        <p class="text-200--bold mb-200">{{t
                            "pricing_assistant.settings.panel.section.rates.matching_rate"
                          }}</p>
                        <div class="flex flex-row items-center justify-start">
                          <GeneralButtonsButtonSelectable
                            @iconName="rates-lowest"
                            @iconSize="small"
                            @isDisabled={{@controller.access.isPricingAssistantPmsSettingsActionOrInputDisabled}}
                            @isSelected={{eq
                              @controller.selectedChannelMatchingRate
                              RATE_TYPES.LOWEST
                            }}
                            @onClick={{fn
                              @controller.updateSelectedChannelMatchingRate
                              RATE_TYPES.LOWEST
                            }}
                            class="text-300--regular"
                          >
                            {{t "rates.lowest"}}
                          </GeneralButtonsButtonSelectable>
                          <GeneralButtonsButtonSelectable
                            @iconName="rates-best-flex"
                            @iconSize="small"
                            @isDisabled={{@controller.access.isPricingAssistantPmsSettingsActionOrInputDisabled}}
                            @isSelected={{eq
                              @controller.selectedChannelMatchingRate
                              RATE_TYPES.BEST_FLEX
                            }}
                            @onClick={{fn
                              @controller.updateSelectedChannelMatchingRate
                              RATE_TYPES.BEST_FLEX
                            }}
                            class="text-300--regular"
                          >
                            {{t "rates.best_flex"}}
                          </GeneralButtonsButtonSelectable>
                        </div>
                      </div>

                      {{! Booking.com offset }}
                      <div class="ml-800">
                        <div class="mb-200 flex flex-row items-center">
                          <p class="text-200--bold mr-100">{{t
                              "general.difference"
                            }}</p>
                          <GeneralIconWithTooltip
                            @side="right"
                            @tooltipContent={{t
                              "pricing_assistant.settings.panel.section.rates.channel_difference_explanation"
                            }}
                          />
                        </div>
                        <div class="flex items-center gap-200">
                          {{#if @controller.isChannelOffsetReadOnly}}
                            <PrismBadge @type="neutral">
                              <PrismIconBrand
                                @name={{@controller.preferredChannelId}}
                              />
                              {{@controller.readOnlyChannelDifferenceValue}}
                            </PrismBadge>

                            <a
                              class="prism-link"
                              href={{@controller.editOffsetUrl}}
                              rel="noopener noreferrer"
                              target="_blank"
                            >
                              {{t
                                "pricing_assistant.settings.panel.section.rates.edit_offset"
                              }}
                              <PrismIcon
                                @color="inherit"
                                @name="link-out"
                                @size="small"
                              />
                            </a>
                          {{else}}
                            <div class="pms-panel-section__input">
                              <PrismNumberInput
                                @disabled={{@controller.access.isPricingAssistantPmsSettingsActionOrInputDisabled}}
                                @inlineLabel={{if
                                  (eq
                                    @controller.selectedChannelOffsetType.id
                                    PMS_SETTINGS_OFFSET_TYPES.RELATIVE
                                  )
                                  "%"
                                  @controller.hotelAndMarketService.hotel.currencySymbol
                                }}
                                @isTouched={{true}}
                                @max={{NUMBER_INPUT_MAX_VALUE}}
                                @onValueChange={{@controller.updateChannelDifferenceValue}}
                                @removePadding={{true}}
                                @required={{true}}
                                @value={{@controller.formattedChannelDifferenceValue}}
                              />
                            </div>

                            <PrismSelectInput
                              @disabled={{@controller.access.isPricingAssistantPmsSettingsActionOrInputDisabled}}
                              @onChange={{@controller.updateChannelOffsetType}}
                              @options={{@controller.offsetTypeOptions}}
                              @value={{@controller.selectedChannelOffsetType}}
                            />
                          {{/if}}
                        </div>
                      </div>
                    </div>
                  </PrismAccordion>
                </div>

                <div class="divider-line mt-400 mb-400"></div>

                {{! Rooms hierarchy and mapping }}
                <div class="pms-panel-section">
                  <PrismAccordion
                    @initiallyOpened={{true}}
                    @title={{t
                      "pricing_assistant.settings.section.rooms.header"
                    }}
                  >
                    <div class="pms-panel-section__content">
                      {{! PMS and booking Leading room mapping }}
                      <div>
                        <div class="mb-200 flex flex-row items-center">
                          <p class="text-200--bold mr-100">{{t
                              "pricing_assistant.settings.panel.section.rooms.leading_room"
                            }}</p>
                          <GeneralIconWithTooltip
                            @side="right"
                            @tooltipContent={{t
                              "pricing_assistant.settings.panel.section.rooms.leading_room_explanation"
                            }}
                          />
                        </div>

                        <div
                          class="pms-roomtype-mapping-row flex flex-row items-center"
                        >
                          <div
                            class="pms-roomtype-mapping-row__pms-room flex flex-col"
                          >
                            <PrismSelectInput
                              @disabled={{or
                                @controller.arePmsPrefillOptionsLoading
                                @controller.access.isPricingAssistantPmsSettingsActionOrInputDisabled
                              }}
                              @onChange={{@controller.updatePmsLeadingRoom}}
                              @options={{@controller.pmsLeadingRoomOptions}}
                              @value={{@controller.selectedPmsLeadingRoom}}
                            />
                          </div>

                          <div
                            class="pms-roomtype-mapping-row__channel-room flex flex-row items-center"
                          >
                            <span class="mr-200">
                              <PrismIconBrand
                                @name={{@controller.preferredChannelId}}
                              />
                            </span>
                            <div
                              class="pms-roomtype-mapping-row__channel-room__select-wrapper flex flex-col"
                            >
                              <PrismSelectInput
                                @disabled={{or
                                  @controller.arePmsPrefillOptionsLoading
                                  @controller.access.isPricingAssistantPmsSettingsActionOrInputDisabled
                                }}
                                @onChange={{fn
                                  @controller.updateMappedChannelRoom
                                  @controller.localSettingsCopy.pms_leading_room_id
                                }}
                                @options={{@controller.channelLeadingRoomOptions}}
                                @value={{@controller.selectedChannelLeadingRoom}}
                              />
                            </div>
                          </div>

                          {{#if
                            @controller.pricingAssistantPmsAccess.hasEditableHierarchy
                          }}
                            {{! Added an empty offset block here in order to align this with room types offsets settings below }}
                            <div class="pms-roomtype-mapping-row__offset"></div>
                          {{/if}}
                        </div>
                      </div>

                      {{! Room types offsets }}
                      {{#if
                        @controller.pricingAssistantPmsAccess.hasEditableHierarchy
                      }}
                        <div>
                          {{#if
                            @controller.hasPmsRoomtypeHierarchyLeadingRoomChildren
                          }}
                            <div
                              class="mb-200 flex flex-row items-center justify-between"
                            >
                              <p class="text-200--bold">
                                {{t
                                  "pricing_assistant.settings.panel.section.rooms_mapping.room_types_offsets"
                                }}

                              </p>

                              <PrismSelectInput
                                @disabled={{@controller.access.isPricingAssistantPmsSettingsActionOrInputDisabled}}
                                @onChange={{@controller.updateRoomtypeOffsetsType}}
                                @options={{@controller.offsetTypeOptions}}
                                @value={{@controller.selectedRoomtypeOffsetsType}}
                              />
                            </div>
                          {{/if}}

                          {{#if @controller.showPmsRoomUnselectionBanner}}
                            <div class="mb-200">
                              <GeneralDismissibleBanner
                                @bannerMessage={{t
                                  "pricing_assistant.settings.room_unselection.banner.message"
                                }}
                                @closeBanner={{@controller.onPmsRoomUnselectionBannerClose}}
                                @isClosable={{true}}
                              />
                            </div>
                          {{/if}}

                          <div
                            class="pms-roomtype-offset-settings-container flex flex-col"
                          >
                            {{#each-in
                              @controller.pmsRoomtypeHierarchyLeadingRoomChildren
                              as |name node|
                            }}
                              <SettingsPmsIntegrationRoomtypeOffset
                                @arePmsPrefillOptionsLoading={{@controller.arePmsPrefillOptionsLoading}}
                                @channelRoomNameToColorMapping={{@controller.channelRoomNameToColorMapping}}
                                @localSettingsCopy={{@controller.localSettingsCopy}}
                                @offsetUnit={{@controller.roomtypeOffsetUnit}}
                                @pmsRoomName={{name}}
                                @pmsRoomNode={{node}}
                                @pmsRoomtypeHierarchyLeadingRoomChildren={{@controller.pmsRoomtypeHierarchyLeadingRoomChildren}}
                                @updateLocalSettingsCopy={{@controller.updateLocalSettingsCopy}}
                              />
                            {{/each-in}}
                          </div>
                        </div>
                      {{/if}}

                      {{#if
                        @controller.hasPmsRoomtypeHierarchyLeadingRoomChildren
                      }}
                        <SettingsPmsIntegrationSeasonalRoomHierarchySection />
                      {{/if}}
                    </div>
                  </PrismAccordion>
                </div>

              </div>

              <div class="pms-settings__panel__footer gap-400">
                <PrismButton
                  @buttonType="secondary"
                  @isDisabled={{@controller.isDiscardButtonDisabled}}
                  @label={{t "pricing_assistant.settings.footer.secondary_btn"}}
                  @onClick={{@controller.resetLocalSettingsCopy}}
                />
                <PrismButton
                  @isDisabled={{@controller.isSaveButtonDisabled}}
                  @label={{t "general.save"}}
                  @onClick={{@controller.onSaveButtonClicked}}
                />
              </div>
            </div>

            <div class="pms-settings__visualisation flex flex-col">
              <div
                class="pms-settings__visualisation--scrollable flex justify-center"
              >
                <SettingsPmsIntegrationRoomtypeHierarchyTreeVisualisation
                  @channelId={{@controller.preferredChannelId}}
                  @channelRoomNameToColorMapping={{@controller.channelRoomNameToColorMapping}}
                  @level={{0}}
                  @localSettingsCopy={{@controller.localSettingsCopy}}
                  @node={{@controller.pmsRoomtypeHierarchy}}
                  @pmsPrefillOptions={{@controller.pmsPrefillOptions}}
                />
              </div>
              <div class="flex justify-center pt-200 pb-200">
                <p class="text-200--regular">{{t
                    "pricing_assistant.settings.visualisation.footer"
                  }}</p>
              </div>
            </div>
          </div>
        {{else}}
          <div class="pms-connect flex flex-col">
            {{#if
              @controller.pricingAssistantPmsSs.isPricingAssistantPMSSelfServiceInProgress
            }}
              <div class="flex h-full items-center justify-center">
                <PrismMessage
                  @description={{t
                    "pricing_assistant.settings.pms_connection_in_progress.subtitle"
                  }}
                  @illustration="100x100_progress"
                  @title={{t
                    "pricing_assistant.settings.pms_connection_in_progress.title"
                  }}
                  data-test-id="pms-ss-connect-pms-in-progress-message"
                />
              </div>
            {{else}}
              {{! Show appropriate info banner for non-admin users }}
              {{#unless
                @controller.access.hasPricingAssistantSettingsEditRights
              }}
                <PrismBanner
                  @bannerMessage={{t
                    "pricing_assistant.settings.connect_pms.non_admin.banner.message"
                  }}
                  @bannerStyle="info"
                  @bannerTitle={{t
                    "pricing_assistant.settings.connect_pms.non_admin.banner.title"
                  }}
                  data-test-id="pms-ss-connect-pms-non-admin-banner"
                />
              {{/unless}}
              <div class="flex h-full items-center justify-center">
                <PrismMessage
                  @description={{t
                    "pricing_assistant.settings.connect_pms.subtitle"
                  }}
                  @illustration="100x100_info"
                  @title={{t "pricing_assistant.settings.connect_pms.title"}}
                  class="flex h-full items-center justify-center"
                >
                  {{! Show "Connect PMS" button only for admin users }}
                  <:primaryAction>
                    {{#if
                      @controller.access.hasPricingAssistantSettingsEditRights
                    }}
                      <HotelPricingAssistantPmsSsConnectPmsButton />
                    {{/if}}
                  </:primaryAction>
                </PrismMessage>
              </div>
            {{/if}}
          </div>
        {{/if}}
      </section>
    </:default>
  </SettingsPageTemplate>
</template>
